interface ICacheRecord<K, V> {
    value: V;
    lastUsed: number;
    expire: number;
    idleDurationMs: number;
    expireCallback: (key: K, value: V) => void;
    expiringTriggerTime: number;
    expiringCallback: (key: K, value: V) => void;
}

export interface ICacheOptions<K, V> {
    sweepInterval?: number;
    idleDurationMs?: number;
    idleCallback?: (key: K, value: V) => void;
    delCallback?: (key: K, value: V) => void;
}

export class Cache<K, V> {
    private readonly cache = new Map<K, ICacheRecord<K, V>>();
    private interval: number;
    private timeout: NodeJS.Timeout;
    private options: ICacheOptions<K, V>;

    public constructor(options?: ICacheOptions<K, V>) {
        this.options = options || { sweepInterval: 100 };

        if (this.options.idleDurationMs != undefined && this.options.idleDurationMs <= 0) {
            throw new Error('Idle duration must be positive');
        }

        this.interval = this.options.sweepInterval || 100;
        if (this.interval <= 0) {
            throw new Error('Sweep interval must be a positive number');
        }
    }

    public put(key: K, value: V, time: number, expireCallback?: (key: K, value: V) => void, idleDurationMs?: number,
    expiringTimeBuffer?: number, expiringCallback?: (key: K, value: V) => void): V {
        if (time === undefined || time === null || time <= 0) {
            throw new Error('Cache timeout must be a positive number');
        }

        if (idleDurationMs === undefined || idleDurationMs === null || idleDurationMs <= 0) {
            idleDurationMs = this.options.idleDurationMs;
        }

        const record: ICacheRecord<K, V> = {
            value: value,
            lastUsed: idleDurationMs ? Date.now() : undefined,
            expire: time + Date.now(),
            idleDurationMs: idleDurationMs,
            expireCallback: expireCallback,
            expiringTriggerTime: expiringTimeBuffer + Date.now(),
            expiringCallback: expiringCallback
        };

        this.cache.set(key, record);

        if (!this.timeout) {
            this.timeout = setInterval(this.onInterval.bind(this), this.interval);

            // In node.js, this timer should be .unref'd.
            // This function doesn't exist in the client
            if (this.timeout.unref) {
                this.timeout.unref();
            }
        }

        return value;
    }

    public del(key: K): boolean {
        if (this.options.delCallback) {
            const record = this.cache.get(key);
            if (record) {
                this.options.delCallback(key, record.value);
            }
        }
        const retVal = this.cache.delete(key);

        // Turn off the timer
        if (this.size() === 0) {
            this.clear();
        }

        return retVal;
    }

    public clear(): void {
        if (this.timeout) {
            clearInterval(this.timeout);
            this.timeout = undefined;
        }

        this.cache.clear();
    }

    public get(key: K): V {
        const record = this.cache.get(key);
        if (record) {
            if (this.options.idleDurationMs) {
                record.lastUsed = Date.now();
            }

            return record.value;
        }

        return undefined;
    }

    /**
     * Does not work for IE. You will need a polyfill for Map if you want to use this on IE.
     * Use forEach instead.
     */
    public keys(): IterableIterator<K> {
        return this.cache.keys();
    }

    public forEach(cb: (value: V, key: K) => void): void {
        this.cache.forEach((record: ICacheRecord<K, V>, key: K): void => {
            cb(record.value, key);
        });
    }

    public size(): number {
        return this.cache.size;
    }

    public updateExpireTime(key: K, timeToLive: number): boolean {
        if (this.cache.has(key) && timeToLive >= 0) {
            this.cache.get(key).expire = timeToLive + Date.now();
            return true;
        }

        return false;
    }

    private onInterval(): void {
        const now = Date.now();

        this.cache.forEach((record, key) => {
            if (record.idleDurationMs) {
                if (record.lastUsed < now - record.idleDurationMs) {
                    this.del(key);
                    if (this.options.idleCallback) {
                        this.options.idleCallback(key, record.value);
                    }
                    return; // Ensure we don't fire an expire callback
                }
            }

            if (record.expire < now) {
                this.del(key);
                if (record.expireCallback) {
                    record.expireCallback(key, record.value);
                }
            }
            // calls callback if record is expiring within buffer time
            if (record.expiringTriggerTime < now) {
                if (record.expiringCallback) {
                    record.expiringCallback(key, record.value);
                    record.expiringCallback = undefined;
                }
            }
        });
    }
}
